import { FormBlock } from '@qbe/ads-blocks-forms';
import {
  Box,
  Button,
  FormGroupLabel,
  RadioSelectableCard,
  RadioSelectableCardProps,
  Typography,
} from '@qbe/ads-core';
import React, { useState } from 'react';

import { ContextualHelpWithButton } from '~/components-ds/ContextualHelp/ContextualHelpWithButton';
import ERRORS from '~/constants/errors';
import { useAppContent, useAppDispatch, useBusinessDetails } from '~/hooks';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { PropertyType } from '~/slices/businessDetails';
import { hardDecline } from '~/slices/decline';
import { qalFormStartAction } from '~/thunks/analytics';
import { updatePropertyTypeAction } from '~/thunks/state-actions';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

export const PropertyDetails = () => {
  const { propertyType } = useBusinessDetails();
  const [type, setType] = useState<PropertyType | null>(propertyType ?? null);
  const dispatch = useAppDispatch();

  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.businessDetailsPropertyOccupancy',
  });

  const handleContinue = () => {
    if (type) dispatch(updatePropertyTypeAction(type));
    if (type !== 'single-tenant') {
      dispatch(
        hardDecline({
          ...ERRORS.occupation,
        })
      );
    }
  };
  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  return (
    <>
      <Box>
        <FormGroupLabel
          title={getTextContent('fields.occupancyType.title')}
          alignCenter
          titleSuffix={
            <ContextualHelpWithButton tracking="help: How is your commercial property occupied?">
              <Typography>
                {getJSXContent('contextualhelpers.occupationType.description')}
              </Typography>
            </ContextualHelpWithButton>
          }
        />
      </Box>

      <RadioSelectableCard
        title=""
        titleHidden
        name="method"
        radioList={
          [
            {
              id: 'single-tenant',
              value: 'single-tenant',
              label: getTextContent('fields.occupancyType.singleTenant'),
              'data-track': 'single tenant',
              'data-track-value': 'capture',
              subText: getTextContent('fields.occupancyType.singleTenantSubtext'),
            },
            {
              id: 'multiple-tenants',
              value: 'multiple-tenants',
              label: getTextContent('fields.occupancyType.multipleTenants'),
              'data-track': 'multiple tenant',
              'data-track-value': 'capture',
            },
            {
              id: 'untenanted',
              value: 'untenanted',
              label: getTextContent('fields.occupancyType.untenanted'),
              'data-track': 'untenanted',
              'data-track-value': 'capture',
            },
            {
              id: 'vacant-land',
              value: 'vacant-land',
              label: getTextContent('fields.occupancyType.vacantLand'),
              'data-track': 'vacant land',
              'data-track-value': 'capture',
            },
          ] as RadioSelectableCardProps['radioList']
        }
        direction="vertical"
        defaultValue={type ?? ''}
        onChange={(v) => {
          handleFormStart();
          setType(v as PropertyType);
          dispatch(updatePropertyTypeAction(undefined));
        }}
      />

      <FormBlock preset="button">
        <Button
          label={getSharedContent('continueStepButttonLabel')}
          onClick={handleContinue}
          data-track="Continue"
          disabled={!type || type === propertyType}
        />
      </FormBlock>
    </>
  );
};
